/**
 * Branded Types - Type-safe ID wrappers using Zod 4.1.9
 * 
 * Zod 4 API: z.string().brand("BrandName") - takes string argument
 * 
 * Branded types prevent mixing up different ID types at compile time.
 * Example: You can't accidentally pass an ApplicationId where an ApplicantId is expected.
 */
import { z } from 'zod';
import {
  AcademicYear,
  ApplicationId,
  UserId,
  UnixTimestamp,
  createBrandedString,
  createBrandedUuid,
  createBrandedUuidLoose,
  dateToUnix,
  nowUnix,
  unixToDate,
} from '@triedge/domain-core/types';

export {
  AcademicYear,
  ApplicationId,
  UserId,
  UnixTimestamp,
  createBrandedString,
  createBrandedUuid,
  createBrandedUuidLoose,
  dateToUnix,
  nowUnix,
  unixToDate,
};

// ============================================================================
// Brand Type Helper (Zod 4 API)
// ============================================================================

// ============================================================================
// Entity IDs (UUIDs)
// ============================================================================

export const ApplicantId = createBrandedUuidLoose('ApplicantId');
export type ApplicantId = z.infer<typeof ApplicantId>;

export const ChildApplicationId = createBrandedUuid('ChildApplicationId');
export type ChildApplicationId = z.infer<typeof ChildApplicationId>;

export const ContactId = createBrandedUuid('ContactId');
export type ContactId = z.infer<typeof ContactId>;

export const DocumentId = createBrandedUuid('DocumentId');
export type DocumentId = z.infer<typeof DocumentId>;

export const NoteId = createBrandedUuid('NoteId');
export type NoteId = z.infer<typeof NoteId>;

export const TimelineEventId = createBrandedUuid('TimelineEventId');
export type TimelineEventId = z.infer<typeof TimelineEventId>;

// ============================================================================
// Human-Readable Codes (Zod 4 API)
// ============================================================================

/**
 * Application code formats:
 * - APP-YYYY-NNNNNN (internal)
 * - YST-YYYY-XXXXX (external UI pattern, alphanumeric)
 */
export const ApplicationCode = z.string()
  .regex(/^(APP-\d{4}-\d{6}|YST-\d{4}-[A-Z0-9]{5})$/, 'Application code format must match APP-YYYY-NNNNNN or YST-YYYY-XXXXX')
  .brand('ApplicationCode');
export type ApplicationCode = z.infer<typeof ApplicationCode>;
